import { signal } from '@preact/signals';
import { defaultSettings, STARTER_LOADOUT } from '@vm/content';
import type { CreateRoomRequest, MatchAssignment, MatchRewards, ModeId, RewardBundle } from '@vm/shared';
import type { MatchLaunch } from '../../game/GameView';
import { offlineTransport, wsTransport } from '../../game/net/Transport';
import { audio } from '../../game/audio/Audio';
import { errorMessage } from '../api/http';
import { fullscreenForPlay } from '../fullscreen';
import { api } from '../api/routes';
import { social } from '../api/social';
import { claimBootCamp, loadAchievements, loadBattlePass, loadQuests, markTutorialDone, refreshMe, requireOnline, rewardToast } from './actions';
import { modeName } from './content';
import { track } from './telemetry';
import { go, route } from './nav';
import { prefs } from './prefs';
import { catalog, cosmetics, currentMatch, lastRewards, loadout, me, party, patchMe, queue, toast } from './store';

export type LaunchKind = 'offline' | 'tutorial' | 'online' | 'dev';

/** In-match "Report player" handler (the report dialog lives in the UI layer). */
let reportHook: ((userId: string, name: string, matchId: string) => void) | null = null;
export function setReportHandler(fn: (userId: string, name: string, matchId: string) => void): void {
  reportHook = fn;
}

/** The running match (GameView is mounted while this is set). */
export const launch = signal<MatchLaunch | null>(null);
export const launchKind = signal<LaunchKind | null>(null);
/** A private room that was created and is waiting for the host to enter. */
export const roomReady = signal<{ match: MatchAssignment; code: string; password?: string; createdAt: number } | null>(null);
/** Level-ups to celebrate once the player is back in the menus. */
export const pendingLevelUp = signal<{ from: number; to: number; rewards: RewardBundle[] } | null>(null);
/** Explanation shown after a match ended unexpectedly. */
export const exitNotice = signal<string | null>(null);

const params = new URLSearchParams(location.search);

function playerInfo(starter = false) {
  const m = me.value;
  return {
    name: m?.name ?? 'You',
    level: m?.progression.level ?? 1,
    clanTag: m?.clan?.tag,
    loadout: starter ? { weapons: [...STARTER_LOADOUT.weapons], gadget: STARTER_LOADOUT.gadget, pet: STARTER_LOADOUT.pet } : loadout.value,
    cosmetics: starter ? { skin: 'preset:marine', armor: 'standard' } : cosmetics.value,
    isBot: false,
    restricted: m?.restricted,
  };
}

function backToMenus(): void {
  launch.value = null;
  launchKind.value = null;
  social.setPresence('lobby');
  document.exitPointerLock?.();
}

// Offline ------------------------------------------------------------------------------------------------------

export interface OfflineOptions {
  skill?: number;
  kind?: 'offline' | 'tutorial';
  /** Use the starter loadout and default look (automation / tests). */
  starter?: boolean;
  name?: string;
}

/**
 * Starts an offline match in a worker. `bots` is the player-count target the sim fills with bots
 * (the local player counts toward it), matching `MatchSettings.bots`.
 */
export function startOffline(mode: ModeId, mapId: string, bots: number, opts: OfflineOptions = {}): void {
  fullscreenForPlay();
  const kind = opts.kind ?? 'offline';
  const settings = defaultSettings(mode, mapId, { bots, botSkill: opts.skill ?? 0.5, private: true });
  // Practice plays with the live weapon balance too (Boot Camp keeps the built-in numbers).
  const balance = catalog.value?.weapons;
  if (kind !== 'tutorial' && balance && (balance.disabled.length || Object.keys(balance.overrides).length)) settings.weapons = structuredClone(balance);
  const info = { ...playerInfo(opts.starter), ...(opts.name ? { name: opts.name } : {}) };
  let completed = false;
  launchKind.value = kind;
  social.setPresence('in_match', kind === 'tutorial' ? 'Boot Camp' : `Practice · ${modeName(mode)}`);
  launch.value = {
    analytics: { mode, map: mapId, kind },
    transport: () => offlineTransport(settings, info),
    ticket: 'offline',
    skin: info.cosmetics.skin,
    armor: info.cosmetics.armor,
    onMatchEnd: () => {
      completed = true;
      // Boot Camp ends (as a win) once every step is done.
      if (kind === 'tutorial') void markTutorialDone();
    },
    onExit: (reason) => {
      backToMenus();
      if (kind === 'tutorial') {
        track('tutorial', { label: completed ? 'complete' : 'quit' });
        void markTutorialDone();
        if (completed) void claimBootCamp();
      } else if (reason && reason !== 'Match ended' && reason !== 'bye') exitNotice.value = reason;
    },
    onRequeue: kind === 'tutorial' ? undefined : () => {
      backToMenus();
      setTimeout(() => startOffline(mode, mapId, bots, opts), 50);
    },
  };
}

export function startTutorial(): void {
  audio.unlock();
  startOffline('tutorial', 'training', 1, { kind: 'tutorial' });
}

export function startSandbox(mapId = 'training'): void {
  audio.unlock();
  startOffline('sandbox', mapId, 0);
}

/** Dev game server with unsigned dev tickets (automation: ?auto=online). */
export function startDevOnline(mode: ModeId, mapId: string, name: string): void {
  const url = params.get('server') ?? `ws://${location.hostname}:7700`;
  const ticket = 'dev:' + JSON.stringify({ name, mode, map: mapId });
  launchKind.value = 'dev';
  launch.value = {
    analytics: { mode, map: mapId, kind: 'dev' },
    transport: () => wsTransport(url),
    ticket,
    skin: 'preset:marine',
    armor: 'standard',
    onExit: (reason) => {
      backToMenus();
      if (reason) exitNotice.value = reason;
    },
    onRequeue: () => {
      backToMenus();
      setTimeout(() => startDevOnline(mode, mapId, name), 50);
    },
  };
}

// Online ---------------------------------------------------------------------------------------------------------

export function launchOnline(match: MatchAssignment, requeueMode?: ModeId): void {
  queue.value = { state: 'idle' };
  roomReady.value = null;
  exitNotice.value = null;
  currentMatch.value = { match, ended: false, startedAt: Date.now() };
  launchKind.value = 'online';
  social.setPresence('in_match', modeName(match.mode));
  audio.unlock();
  const exit = (reason?: string, requeue = false): void => {
    const cm = currentMatch.value;
    backToMenus();
    if (cm?.ended) {
      if (!requeue) go('results', { matchId: cm.match.matchId }, false);
    } else if (reason && reason !== 'bye') {
      exitNotice.value = reason;
    }
    void refreshMe();
  };
  launch.value = {
    analytics: { mode: match.mode, map: match.mapId, kind: match.roomCode ? 'room' : 'online' },
    transport: () => wsTransport(match.serverUrl),
    ticket: match.ticket,
    skin: cosmetics.value.skin,
    armor: cosmetics.value.armor,
    onExit: (reason) => exit(reason),
    onReport: (userId, name, matchId) => reportHook?.(userId, name, matchId || match.matchId),
    onMatchEnd: () => {
      const cm = currentMatch.value;
      if (cm && cm.match.matchId === match.matchId) currentMatch.value = { ...cm, ended: true };
    },
    onRequeue: requeueMode
      ? () => {
          exit(undefined, true);
          void quickPlay(requeueMode, prefs.value.maps[requeueMode]);
        }
      : undefined,
  };
}

export function isPartyMember(): boolean {
  const p = party.value;
  return !!p && p.members.length > 1 && p.leaderId !== me.value?.id;
}

export async function quickPlay(mode: ModeId, mapId?: string): Promise<void> {
  if (!requireOnline('Online matches')) return;
  audio.unlock();
  if (isPartyMember()) {
    toast('Your party leader starts the match', { kind: 'info', body: 'Mark yourself ready so they know you are set.' });
    return;
  }
  if (queue.value.state !== 'idle' || launch.value) return;
  fullscreenForPlay();
  queue.value = { state: 'searching', mode, mapId, since: Date.now(), estimate: 0 };
  try {
    const res = await api.play.quick({ mode, mapId: mapId || undefined });
    if (res.state === 'matched') {
      launchOnline(res.match, mode);
    } else if (queue.value.state === 'searching') {
      queue.value = { state: 'searching', mode, mapId, since: queue.value.since, estimate: res.estimatedWaitSec, queueId: res.queueId };
    }
  } catch (e) {
    queue.value = { state: 'idle' };
    toast("Couldn't start matchmaking", { kind: 'error', body: errorMessage(e) });
  }
}

export async function cancelQueue(): Promise<void> {
  if (queue.value.state === 'idle') return;
  queue.value = { state: 'idle' };
  audio.play('ui_click');
  try {
    await api.play.cancel();
  } catch {
    /* the server drops stale queue entries on its own */
  }
}

export async function createRoom(req: CreateRoomRequest): Promise<boolean> {
  if (!requireOnline('Private rooms')) return false;
  try {
    const match = await api.rooms.create(req);
    roomReady.value = { match, code: match.roomCode ?? '', password: req.password, createdAt: Date.now() };
    audio.play('ui_buy');
    return true;
  } catch (e) {
    toast("Couldn't create the room", { kind: 'error', body: errorMessage(e) });
    return false;
  }
}

export async function joinRoom(code: string, password?: string): Promise<boolean> {
  if (!requireOnline('Private rooms')) return false;
  fullscreenForPlay();
  try {
    const match = await api.rooms.join({ code: code.trim().toUpperCase(), password: password || undefined });
    launchOnline(match);
    return true;
  } catch (e) {
    toast("Couldn't join that room", { kind: 'error', body: errorMessage(e) });
    return false;
  }
}

/** Enters a created room; tickets live ~60 s, so an old one is swapped for a fresh join. */
export async function enterRoom(): Promise<void> {
  const r = roomReady.value;
  if (!r) return;
  fullscreenForPlay();
  if (Date.now() - r.createdAt > 45_000 && r.code) {
    const ok = await joinRoom(r.code, r.password);
    if (ok) roomReady.value = null;
    return;
  }
  launchOnline(r.match);
}

// Social socket pushes -------------------------------------------------------------------------------------------

export function onMatchFound(match: MatchAssignment): void {
  if (launch.value && (launchKind.value === 'online' || launchKind.value === 'dev')) return;
  if (launch.value) backToMenus();
  audio.play('capture');
  launchOnline(match, match.mode);
}

export function onQueueUpdate(state: 'searching' | 'cancelled', waited: number): void {
  if (state === 'cancelled') {
    if (queue.value.state !== 'idle') toast('Matchmaking cancelled', { kind: 'info' });
    queue.value = { state: 'idle' };
    return;
  }
  const q = queue.value;
  if (q.state === 'searching') return;
  const mode = party.value?.mode ?? prefs.value.mode;
  queue.value = { state: 'searching', mode, since: Date.now() - waited * 1000, estimate: 0 };
}

export function onRewards(r: MatchRewards): void {
  lastRewards.value = r;
  patchMe({ wallet: r.wallet, progression: r.progression });
  // The results screen presents rewards for the match just played (now, or when the player leaves it).
  const cm = currentMatch.value;
  const shownByResults = cm?.match.matchId === r.matchId && ((launchKind.value === 'online' && !!launch.value) || route.value.screen === 'results');
  if (!shownByResults) {
    rewardToast(r.won ? 'Victory rewards' : 'Match rewards', { xp: r.xp, coins: r.coins, bpXp: r.bpXp });
    if (r.levelAfter > r.levelBefore) pendingLevelUp.value = { from: r.levelBefore, to: r.levelAfter, rewards: r.levelRewards };
  }
  void loadQuests().catch(() => {});
  void loadBattlePass().catch(() => {});
  void loadAchievements().catch(() => {});
  void refreshMe();
}
